/* The bridge from the engine to the renderer. It packs a frame's `World` (the live waves) and
   the active wave's compiled face effects (face-chain.ts) into a flat, typed `RenderState` for
   the WebGL layers.

   The face and the lens scatter in a ring that grows out from the butterfly. Each wave is just
   its front's mean radius and its band's thickness (core/gust.ts), with no center or edge, so
   the band spreads outward from the flap. The shaders read it with `RIPPLE_BAND_GLSL`. */

import { MAX_LIVE_RIPPLES, RIPPLE_DEFAULTS, rippleFrontInto } from "../core/ripple.js";
import { RIPPLE_BAND_LEAD, RIPPLE_BAND_TRAIL, type GustFront } from "../core/gust.js";
import type { World, Ripple } from "../core/types.js";
import type { CompiledOp, ComponentParams } from "../effects/face-chain.js";
import { CAPS } from "../effects/face-chain.js";

/** How far a wave travels, in pixels, before its amplitude drops by a factor of e
    (`RIPPLE.decay`). */
export const RIPPLE_DECAY = 1300;
/** How wide the tail is behind the band's sharp front. The z<0 side (behind the front, toward
    the origin) falls off as `exp(-z²·TAIL)`, so a smaller value means a longer wake. */
export const RIPPLE_BAND_TAIL = 0.3;

/** The band's amplitude when the front is at `radius`, for a wave of amplitude `amp`. */
export function rippleAmplitude(amp: number, radius: number): number {
  return amp * Math.exp(-radius / RIPPLE_DECAY);
}

function glslFloat(value: number): string {
  return Number.isInteger(value) ? value.toFixed(1) : String(value);
}

/** The band's cross-section for the face and lens shaders, at a point's offset `z` from a
    wave's ring (`ringOffset()`, in band thicknesses). It has a sharp front (z≥0) and a long
    tail (z<0), so each point gets a quick hit and then a slow wake, the same shape as the
    wing's fold. */
export const RIPPLE_BAND_GLSL = `
const float BAND_TAIL = ${glslFloat(RIPPLE_BAND_TAIL)};
const float BAND_LEAD = ${glslFloat(RIPPLE_BAND_LEAD)};
const float BAND_TRAIL = ${glslFloat(RIPPLE_BAND_TRAIL)};
float gustBand(float z) {
  return z >= 0.0 ? exp(-z * z) : exp(-z * z * BAND_TAIL);
}
float rippleInfluence(float z, float amplitude) {
  if (z > BAND_LEAD || z < -BAND_TRAIL) return 0.0;
  return amplitude * gustBand(z);
}
// A point's offset from a wave's ring (RenderState.rings: mean radius, thickness), in band
// thicknesses. fromOrigin is the point's position relative to the butterfly, in page pixels.
float ringOffset(vec2 fromOrigin, vec2 ring) {
  return (length(fromOrigin) - ring.x) / ring.y;
}
`;

/** The size of the wave arrays in the face and lens shaders: as many waves as the engine lets
    live at once (`RippleSystem.canEmit`). The packer keeps the most recent ones. */
export const MAX_RIPPLES = MAX_LIVE_RIPPLES;

/** Each face op's number in the shader's `uOpType` (0: none). */
export const FACE_OP_TYPE = {
  dotScale: 1,
  dotJitter: 2,
  dropout: 3,
  pixelate: 4,
  paletteRoll: 5,
} as const;

/** `type` is the op's `FACE_OP_TYPE`, or 0 for none. */
export interface FaceOpUniform {
  type: number;
  k: number;
  edge: number;
  crest: number;
  p0: number;
}

export interface RenderState {
  time: number;
  reducedMotion: boolean;
  /** The scale from artwork units to page pixels (the portrait's `fScale`). The band math, the
      jitter, and the pixelate all run in page pixels, so the face's ring lines up with the
      text's ring and they read as one. The default, 1, leaves artwork units as they are. */
  artworkToPage: number;
  /** How many waves `rings` and `amplitudes` hold, oldest first. */
  rippleCount: number;
  /** Each wave's ring around the butterfly, in page pixels, two floats per wave: its front's
      mean radius and its band's thickness. */
  rings: Float32Array;
  /** Each wave's band amplitude at its mean radius. */
  amplitudes: Float32Array;
  faceOps: FaceOpUniform[];
}

/** Writes the op's shader type (`FACE_OP_TYPE`) and its one rolled parameter into `u`:
    `dotScale`'s `k`, `dotJitter`'s `px`, `dropout`'s `frac`, `pixelate`'s `n`, or
    `paletteRoll`'s `d`. Face chains only hold face ops, so any other id means something's
    wired wrong. */
function writeFaceOp(u: FaceOpUniform, p: ComponentParams): void {
  const set = (type: number, p0: number): void => {
    u.type = type;
    u.p0 = p0;
  };
  switch (p.id) {
    case "dotScale":
      return set(FACE_OP_TYPE.dotScale, p.k);
    case "dotJitter":
      return set(FACE_OP_TYPE.dotJitter, p.px);
    case "dropout":
      return set(FACE_OP_TYPE.dropout, p.frac);
    case "pixelate":
      return set(FACE_OP_TYPE.pixelate, p.n);
    case "paletteRoll":
      return set(FACE_OP_TYPE.paletteRoll, p.d);
    case "amplify":
    case "temper":
    case "invert":
    case "crest":
    case "ghost":
    case "grayscale":
      throw new Error(`render-state: "${p.id}" is not a face op`);
    default:
      return p satisfies never;
  }
}

// The face ops for a frame with no wave: one shared empty list, not a new one every frame.
const NO_FACE_OPS: readonly CompiledOp[] = [];

/** A reusable `RenderState`. Make it once, then refill it every frame with
    `buildRenderStateInto`, so the render loop doesn't make new objects. */
export function createRenderStateScratch(): RenderState {
  return {
    time: 0,
    reducedMotion: false,
    artworkToPage: 1,
    rippleCount: 0,
    rings: new Float32Array(MAX_RIPPLES * 2),
    amplitudes: new Float32Array(MAX_RIPPLES),
    faceOps: [],
  };
}

// One wave's front at the frame's time, refilled for each wave.
const front: GustFront = { x: 0, y: 0, radius: 0, thickness: 0, growth: 0 };

/** Fills `out` from `world` (the live waves) and `activeWave` (the wave whose face effects
    run, or `null`). It keeps at most `MAX_RIPPLES` waves (the most recent) and `CAPS.ops` face
    ops. It reuses `out.rings`, `out.amplitudes`, and `out.faceOps`, so once things settle, a
    frame allocates nothing. */
export function buildRenderStateInto(
  out: RenderState,
  world: World,
  activeWave: Ripple | null,
  artworkToPage = 1,
): void {
  out.time = world.time;
  out.reducedMotion = world.reducedMotion;
  out.artworkToPage = artworkToPage;

  const items = world.ripples.items;
  const rc = Math.min(items.length, MAX_RIPPLES);
  const rStart = items.length - rc; // keep the most recent `rc`
  out.rippleCount = rc;
  for (let i = 0; i < rc; i++) {
    const src = items[rStart + i]!;
    rippleFrontInto(front, src, world.time, RIPPLE_DEFAULTS.dur);
    out.rings[2 * i] = front.radius;
    out.rings[2 * i + 1] = front.thickness;
    out.amplitudes[i] = rippleAmplitude(src.amp, front.radius);
  }

  const ops = activeWave?.faceOps ?? NO_FACE_OPS;
  const oc = Math.min(ops.length, CAPS.ops);
  out.faceOps.length = oc;
  for (let i = 0; i < oc; i++) {
    const op = ops[i]!;
    let u = out.faceOps[i];
    if (!u) {
      u = { type: 0, k: 0, edge: 0, crest: 0, p0: 0 };
      out.faceOps[i] = u;
    }
    writeFaceOp(u, op.p);
    u.k = op.k;
    u.edge = op.edge ? 1 : 0;
    u.crest = op.crest;
  }
}

/** Makes a new `RenderState`, for tests and one-off frames. The render loop should reuse one
    with `buildRenderStateInto`. */
export function buildRenderState(
  world: World,
  activeWave: Ripple | null,
  artworkToPage = 1,
): RenderState {
  const out = createRenderStateScratch();
  buildRenderStateInto(out, world, activeWave, artworkToPage);
  return out;
}
